import { reactive } from 'vue';
import { envConfig } from '../../env.config.js';
import { getCookieValue, isLoggedIn } from './utils';

const FLAGS_TIMEOUT_MS = 2000;

export const flagsState = reactive({ ready: false, loaded: false, flags: {} });

let posthog = null;
let status = 'idle'; // 'idle' | 'loading' | 'active' | 'disabled'
let importStarted = false;
let flagsRequested = false;
let flagsLocked = false;
let flagsSubscribed = false;
let flagsTimer = null;

function hasStatsConsent() {
  const raw = getCookieValue('cookieConsents');
  if (!raw) {
    return false;
  }
  try {
    return JSON.parse(raw).cookieStats === true;
  } catch (e) {
    return false;
  }
}

function isConfigured() {
  return Boolean(envConfig.posthog?.key) && !window.PHP?.bot;
}

export function initPostHog() {
  window.addEventListener('zt:consent-changed', onConsentChanged);
  if (!isConfigured() || !hasStatsConsent()) {
    disable();
    return;
  }
  start();
}

function onConsentChanged(event) {
  const statsAllowed = event.detail?.cookieStats === true;
  if (statsAllowed && status === 'disabled' && isConfigured()) {
    start();
  } else if (!statsAllowed && (status === 'active' || status === 'loading')) {
    stop();
  }
}

function start() {
  if (posthog) {
    afterLoad(posthog);
    return;
  }
  status = 'loading';
  if (importStarted) {
    return;
  }
  importStarted = true;
  import('posthog-js')
    .then(({ default: ph }) => {
      ph.init(envConfig.posthog.key, {
        api_host: envConfig.posthog.host,
        person_profiles: 'identified_only',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_exceptions: false,
        capture_performance: false,
        disable_surveys: true,
        advanced_disable_feature_flags_on_first_load: true,
        opt_out_persistence_by_default: true,
        remote_config_refresh_interval_ms: 0,
        loaded: (instance) => {
          posthog = instance;
          afterLoad(instance);
        },
      });
    })
    .catch(() => disable());
}

function afterLoad(ph) {
  try {
    if (!hasStatsConsent()) {
      stop();
      return;
    }
    if (ph.has_opted_out_capturing()) {
      ph.opt_in_capturing({ captureEventName: false });
    }
    ph.set_config({ advanced_disable_flags: false });
    syncIdentity(ph);
    ph.register({ environment: envConfig.posthog.environment });
    status = 'active';
    if (flagsRequested && !flagsLocked) {
      requestFlags();
    }
  } catch (e) {
    disable();
  }
}

function syncIdentity(ph) {
  const user = BACKEND_DATA.data.user;
  if (isLoggedIn()) {
    const userId = String(user.id);
    if (ph.get_distinct_id() !== userId) {
      ph.identify(userId, { role: user.role });
    }
  } else if (ph.get_property('$user_id')) { // wylogowanie
    ph.reset();
  }
}

function stop() {
  if (posthog) {
    posthog.set_config({ advanced_disable_flags: true });
    posthog.reset();
    posthog.opt_out_capturing();
  }
  disable();
}

function disable() {
  status = 'disabled';
  clearFlagsTimer();
  flagsState.flags = {};
  flagsState.loaded = false;
  flagsState.ready = true;
}

export function loadFlags() {
  if (flagsRequested) {
    return;
  }
  flagsRequested = true;
  if (status === 'disabled') {
    return;
  }
  flagsTimer = setTimeout(onFlagsTimeout, FLAGS_TIMEOUT_MS);
  if (status === 'active') {
    requestFlags();
  }
}

function requestFlags() {
  if (!flagsSubscribed) {
    flagsSubscribed = true;
    posthog.onFeatureFlags(onFlags);
  }
  posthog.reloadFeatureFlags();
}

function onFlags(flags, variants, context) {
  if (flagsLocked || status !== 'active') {
    return;
  }
  clearFlagsTimer();
  if (context?.errorsLoading) {
    flagsLocked = true;
    flagsState.ready = true;
    return;
  }
  flagsState.flags = { ...variants };
  flagsState.loaded = true;
  flagsState.ready = true;
}

function onFlagsTimeout() {
  flagsTimer = null;
  if (flagsState.ready) {
    return;
  }
  flagsLocked = true;
  flagsState.ready = true;
}

function clearFlagsTimer() {
  if (flagsTimer) {
    clearTimeout(flagsTimer);
    flagsTimer = null;
  }
}

export function resolveVariant(key) {
  const value = flagsState.flags[key];
  if (value === true) {
    return 'test';
  }
  if (typeof value === 'string' && value !== '') {
    return value;
  }
  return 'control';
}

export function reportFlagExposure(key) {
  if (status !== 'active' || !flagsState.loaded) {
    return;
  }
  try {
    posthog.getFeatureFlag(key);
  } catch (e) {}
}

export function trackEvent(name, properties = {}) {
  if (status !== 'active') {
    return;
  }
  try {
    posthog.capture(name, properties, { send_instantly: true });
  } catch (e) {}
}
